import React, { useState } from 'react';
import { FaArrowUpRightFromSquare, FaRotate } from 'react-icons/fa6';
import { PrimaryButton, SecondaryButton } from '@/components/Buttons';
import { providerPresentation, oauthLinkFor } from '../providerCatalog';

/**
 * Authorization for a provider whose OAuth handshake is served by another
 * service.
 *
 * Nothing is posted from here. The catalogue hands us the provider's
 * authorization URL (templated with `{store_id}`), the operator completes the
 * grant on that service, and the connection shows up on the store record — so
 * the only thing left for this panel to do afterwards is re-read the store.
 *
 * A new tab rather than a popup: the handshake finishes on an origin we don't
 * control and never posts back to `window.opener`, so there is no message to
 * listen for and a popup would just be a worse window.
 */
const OAuthLinkPanel = ({ descriptor, storeId, onClose, onConnectionSuccess }) => {
  const presentation = providerPresentation(descriptor);
  const link = oauthLinkFor(descriptor, storeId);
  const [opened, setOpened] = useState(false);
  const [checking, setChecking] = useState(false);

  const handleAuthorize = () => {
    window.open(link, '_blank', 'noopener,noreferrer');
    setOpened(true);
  };

  const handleCheck = async () => {
    setChecking(true);
    try {
      if (onConnectionSuccess) await onConnectionSuccess();
      onClose();
    } finally {
      setChecking(false);
    }
  };

  if (!link) {
    return (
      <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
        <p className="text-sm text-amber-800 dark:text-amber-200">
          {presentation.name} authorizes by OAuth, but the catalogue returned no
          authorization link for it. Check that a master record exists for this provider.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {descriptor?.instructions && (
        <div className="rounded-lg border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/20 p-4">
          <p className="text-sm text-sky-800 dark:text-sky-200 whitespace-pre-line">
            {descriptor.instructions}
          </p>
        </div>
      )}

      <p className="text-sm text-slate-600 dark:text-slate-300">
        {presentation.name} is connected by authorizing Saucey from your{' '}
        {presentation.name} account. This opens in a new tab; come back here once
        it&rsquo;s done.
      </p>

      <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-3">
        <p className="text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1">
          Authorization link
        </p>
        <p className="font-mono text-xs text-slate-700 dark:text-slate-200 break-all">{link}</p>
      </div>

      {opened && (
        <div className="rounded-lg border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/20 p-3">
          <p className="text-sm text-sky-800 dark:text-sky-200">
            Finish the authorization in the other tab, then choose{' '}
            <strong>I&rsquo;ve authorized</strong> to re-check this store.
          </p>
        </div>
      )}

      <div className="flex items-center justify-end gap-3 flex-wrap pt-1">
        <SecondaryButton onClick={onClose} disabled={checking}>
          Cancel
        </SecondaryButton>
        {opened && (
          <SecondaryButton onClick={handleCheck} disabled={checking} loading={checking}>
            <span className="inline-flex items-center gap-2">
              <FaRotate className="w-3.5 h-3.5" />
              I&rsquo;ve authorized
            </span>
          </SecondaryButton>
        )}
        <PrimaryButton onClick={handleAuthorize}>
          <span className="inline-flex items-center gap-2">
            <FaArrowUpRightFromSquare className="w-3.5 h-3.5" />
            Authorize with {presentation.name}
          </span>
        </PrimaryButton>
      </div>
    </div>
  );
};

export default OAuthLinkPanel;
